import { env } from '@/config/env';
import {
  PrFilterState,
  PrMetricsSummary,
  PrRecord,
  PrStatusKey,
} from '@/types/domain/pr-management';
import { MOCK_PRS } from '@/mocks/database';
import { processQuery } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';
import { mockPurchaseRequestService } from './MockPurchaseRequestService';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_PR_RECORDS: PrRecord[] = MOCK_PRS.map((pr, i) => ({
  id: i === 0 ? 'PR-10001' : i === 2 ? 'PR-10003' : pr.id,
  prNumber: i === 0 ? 'PR-10001' : i === 2 ? 'PR-10003' : pr.prNumber,
  requestNumber: i === 0 ? 'REQ-1001' : pr.requestNumber,
  title: pr.title,
  justification: 'Automated Purchase Requisition generated from approved Purchase Request.',
  requesterId: 'usr-1',
  requesterName: pr.requesterName,
  departmentName: pr.departmentName,
  companyCode: '1000',
  plantCode: pr.plantCode,
  costCenterCode: 'CC-IT-101',
  purchasingOrg: '1000',
  purchasingGroup: 'PGRP-001',
  priority: i % 10 === 0 ? 'HIGH' : 'MEDIUM',
  status: (i === 0 ? 'SAP_CREATED' : i === 2 ? 'VALIDATION_FAILED' : pr.status === 'SAP_CREATED' ? 'SAP_CREATED' : 'SUBMITTED') as PrStatusKey,
  totalAmount: pr.totalValue,
  currency: pr.currency,
  requiredDate: '2026-10-30',
  items: [
    {
      id: `PR-ITEM-${i + 1}-1`,
      itemNumber: 10,
      materialCode: `MAT-900${(i % 10) + 1}`,
      description: 'Enterprise Spec Hardware Item',
      category: 'IT Hardware',
      quantity: 10,
      uom: 'EA',
      unitPrice: Math.round(pr.totalValue / 10),
      totalPrice: pr.totalValue,
      currency: pr.currency,
      plantCode: pr.plantCode,
      deliveryDate: '2026-10-30',
      costCenterCode: 'CC-IT-101',
      glAccountCode: '500100',
    },
  ],
  financialSummary: {
    subtotal: pr.totalValue,
    taxAmount: Math.round(pr.totalValue * 0.18),
    totalAmount: pr.totalValue * 1.18,
    currency: pr.currency,
    budgetLimit: 1000000,
    budgetConsumed: 350000,
    availableBudget: 650000,
    budgetStatus: 'SUFFICIENT',
  },
  sapValidation: {
    companyCode: true,
    plant: true,
    material: true,
    costCenter: true,
    glAccount: true,
    purchasingGroup: true,
    currency: true,
    quantity: true,
    deliveryDate: true,
    budget: true,
    isValid: true,
  },
  sapDetails: {
    sapPrNumber: i === 0 ? '45000123' : pr.sapPrNumber || `45000${100 + i}`,
    sapStatus: pr.status === 'SAP_CREATED' ? 'CREATED' : 'PROCESSING',
    integrationStatus: pr.status === 'SAP_CREATED' ? 'SYNCED' : 'PENDING',
    lastSyncTimestamp: pr.updatedAt,
  },
  lifecycle: [
    { key: 'SUBMITTED', label: 'PR Created', status: 'COMPLETED', timestamp: pr.createdAt },
  ],
  nfaId: `NFA-${1000 + i}`,
  createdAt: pr.createdAt,
  updatedAt: pr.updatedAt,
  version: 1,
}));

export class MockPrManagementService {
  private records: PrRecord[] = [...GENERATED_PR_RECORDS];

  async getPrMetrics(): Promise<PrMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalPrs = this.records.length;
    const pendingApproval = this.records.filter((r) => r.status === 'PENDING_APPROVAL').length;
    const approved = this.records.filter((r) => r.status === 'APPROVED' || r.status === 'READY_FOR_SAP').length;
    const sapProcessing = this.records.filter((r) => r.status === 'SAP_PROCESSING').length;
    const sapCreated = this.records.filter((r) => r.status === 'SAP_CREATED').length;
    const validationFailed = this.records.filter((r) => r.status === 'VALIDATION_FAILED' || r.status === 'SAP_FAILED').length;
    const highValuePrs = this.records.filter((r) => r.totalAmount > 100000).length;

    return {
      totalPrs,
      pendingApproval,
      approved,
      sapProcessing,
      sapCreated,
      validationFailed,
      highValuePrs,
    };
  }

  async getPrRecords(filters?: Partial<PrFilterState>): Promise<{ items: PrRecord[]; total: number }> {
    await delay(env.mockDelayMs);
    const queryResult = processQuery(this.records, {
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25,
      search: filters?.search,
      filters: {
        status: filters?.status,
        department: filters?.department,
        plant: filters?.plant,
        costCenter: filters?.costCenter,
        priority: filters?.priority,
      },
    });

    return { items: queryResult.data, total: queryResult.total };
  }

  async getPrs(filters?: any): Promise<{ items: PrRecord[]; total: number }> {
    return this.getPrRecords(filters);
  }

  async getPrById(id: string): Promise<PrRecord | null> {
    await delay(env.mockDelayMs);
    const cleanId = String(id).trim().toLowerCase();

    const cleanDigits = cleanId.replace(/[^0-9]/g, '');

    // 1. Direct search in PR records by id, prNumber, requestNumber, or sapPrNumber
    const found = this.records.find(
      (r) =>
        String(r.id || '').toLowerCase() === cleanId ||
        String(r.prNumber || '').toLowerCase() === cleanId ||
        String(r.requestNumber || '').toLowerCase() === cleanId ||
        (r.sapDetails?.sapPrNumber && String(r.sapDetails.sapPrNumber).toLowerCase() === cleanId) ||
        (cleanDigits &&
          (String(r.id || '').replace(/[^0-9]/g, '') === cleanDigits ||
            String(r.prNumber || '').replace(/[^0-9]/g, '') === cleanDigits ||
            String(r.requestNumber || '').replace(/[^0-9]/g, '') === cleanDigits ||
            (r.sapDetails?.sapPrNumber && String(r.sapDetails.sapPrNumber).replace(/[^0-9]/g, '') === cleanDigits)))
    );
    if (found) return JSON.parse(JSON.stringify(found));

    // 2. Cross-reference with MockPurchaseRequestService
    const prSource = mockPurchaseRequestService.findRequestInMemory(id);
    if (prSource) {
      const seq = prSource.requestNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
      const newPrRecord: PrRecord = {
        id: prSource.id,
        prNumber: `PR-${seq}`,
        requestNumber: prSource.requestNumber,
        title: prSource.title,
        justification: prSource.justification || 'Departmental procurement requirement.',
        requesterId: prSource.requesterId || 'usr-1',
        requesterName: prSource.requesterName || 'Requester',
        departmentName: prSource.departmentName,
        companyCode: '1000',
        plantCode: prSource.plantCode || 'PLANT-1000',
        costCenterCode: prSource.costCenterCode || 'CC-IT-101',
        purchasingOrg: '1000',
        purchasingGroup: 'PGRP-001',
        priority: prSource.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
        status: 'SAP_CREATED',
        totalAmount: prSource.totalAmount,
        currency: prSource.currency,
        requiredDate: prSource.requiredDate || '2026-10-30',
        items: (prSource.items || []).map((it, idx) => ({
          id: `PR-ITEM-${seq}-${idx + 1}`,
          itemNumber: (idx + 1) * 10,
          materialCode: it.materialCode,
          description: it.materialName || prSource.title,
          category: prSource.category,
          quantity: it.quantity,
          uom: it.unitOfMeasure || 'EA',
          unitPrice: it.estimatedUnitPrice || 0,
          totalPrice: it.totalEstimatedPrice || it.quantity * (it.estimatedUnitPrice || 0),
          currency: prSource.currency,
          plantCode: prSource.plantCode || 'PLANT-1000',
          deliveryDate: prSource.requiredDate || '2026-10-30',
          costCenterCode: prSource.costCenterCode || 'CC-IT-101',
          glAccountCode: '500100',
        })),
        financialSummary: {
          subtotal: prSource.totalAmount,
          taxAmount: Math.round(prSource.totalAmount * 0.18),
          totalAmount: Math.round(prSource.totalAmount * 1.18),
          currency: prSource.currency,
          budgetLimit: Math.round(prSource.totalAmount * 2),
          budgetConsumed: prSource.totalAmount,
          availableBudget: prSource.totalAmount,
          budgetStatus: 'SUFFICIENT',
        },
        sapValidation: {
          companyCode: true,
          plant: true,
          material: true,
          costCenter: true,
          glAccount: true,
          purchasingGroup: true,
          currency: true,
          quantity: true,
          deliveryDate: true,
          budget: true,
          isValid: true,
        },
        sapDetails: {
          sapPrNumber: `45000${seq}`,
          sapStatus: 'CREATED',
          createdTimestamp: new Date().toISOString(),
          lastSyncTimestamp: new Date().toISOString(),
          integrationStatus: 'SYNCED',
        },
        lifecycle: [
          { key: 'CREATED', label: '1. Requisition Approved', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
          { key: 'SAP_VALIDATED', label: '2. SAP BAPI Validated', status: 'COMPLETED', docRef: `45000${seq}`, timestamp: new Date().toISOString().slice(0, 10) },
          { key: 'COMMERCIAL', label: '3. NFA Commercial Signoff', status: 'ACTIVE' },
          { key: 'SOURCING', label: '4. RFQ Sourcing', status: 'PENDING' },
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
      };
      this.records.unshift(newPrRecord);
      return JSON.parse(JSON.stringify(newPrRecord));
    }

    // 3. Fallback: synthesize valid record with full details so UI never fails
    const seq = cleanId.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
    const fallbackPr: PrRecord = {
      id,
      prNumber: id.toUpperCase().startsWith('PR-') ? id.toUpperCase() : `PR-${seq}`,
      requestNumber: id.toUpperCase().startsWith('REQ-') ? id.toUpperCase() : `REQ-${seq}`,
      title: `Enterprise Purchase Requisition #${seq}`,
      justification: 'Automated requisition record for procurement lifecycle verification.',
      requesterId: 'usr-1',
      requesterName: 'Alexander Hamilton',
      departmentName: 'Information Technology',
      companyCode: '1000',
      plantCode: 'PLANT-1000',
      costCenterCode: 'CC-IT-101',
      purchasingOrg: '1000',
      purchasingGroup: 'PGRP-001',
      priority: 'MEDIUM',
      status: 'SAP_CREATED',
      totalAmount: 125000,
      currency: 'INR',
      requiredDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      items: [
        {
          id: `PR-ITEM-${seq}-1`,
          itemNumber: 10,
          materialCode: `MAT-${seq}`,
          description: `Enterprise Material Hardware Specification #${seq}`,
          category: 'CapitalEquipment',
          quantity: 2,
          uom: 'EA',
          unitPrice: 62500,
          totalPrice: 125000,
          currency: 'INR',
          plantCode: 'PLANT-1000',
          deliveryDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
          costCenterCode: 'CC-IT-101',
          glAccountCode: '500100',
        },
      ],
      financialSummary: {
        subtotal: 125000,
        taxAmount: 22500,
        totalAmount: 147500,
        currency: 'INR',
        budgetLimit: 500000,
        budgetConsumed: 125000,
        availableBudget: 375000,
        budgetStatus: 'SUFFICIENT',
      },
      sapValidation: {
        companyCode: true,
        plant: true,
        material: true,
        costCenter: true,
        glAccount: true,
        purchasingGroup: true,
        currency: true,
        quantity: true,
        deliveryDate: true,
        budget: true,
        isValid: true,
      },
      sapDetails: {
        sapPrNumber: `45000${seq}`,
        sapStatus: 'CREATED',
        createdTimestamp: new Date().toISOString(),
        lastSyncTimestamp: new Date().toISOString(),
        integrationStatus: 'SYNCED',
      },
      lifecycle: [
        { key: 'CREATED', label: '1. Requisition Approved', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'SAP_VALIDATED', label: '2. SAP BAPI Validated', status: 'COMPLETED', docRef: `45000${seq}`, timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'COMMERCIAL', label: '3. NFA Commercial Signoff', status: 'ACTIVE' },
        { key: 'SOURCING', label: '4. RFQ Sourcing', status: 'PENDING' },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };
    this.records.unshift(fallbackPr);
    return JSON.parse(JSON.stringify(fallbackPr));
  }

  async createPr(payload: Partial<PrRecord>): Promise<PrRecord> {
    await delay(env.mockDelayMs);
    const seq = Math.floor(1000 + Math.random() * 9000);
    const prNumber = `PR-${seq}`;

    const newRecord: PrRecord = {
      id: prNumber,
      prNumber,
      requestNumber: payload.requestNumber || `REQ-${seq}`,
      title: payload.title || 'New Purchase Requisition',
      justification: payload.justification || 'Standard PR justification',
      requesterId: payload.requesterId || 'usr-1',
      requesterName: payload.requesterName || 'Sarah Jenkins',
      departmentName: payload.departmentName || 'Information Technology',
      companyCode: payload.companyCode || '1000',
      plantCode: payload.plantCode || 'PLANT-1000',
      costCenterCode: payload.costCenterCode || 'CC-IT-101',
      purchasingOrg: payload.purchasingOrg || '1000',
      purchasingGroup: payload.purchasingGroup || 'PGRP-001',
      priority: payload.priority || 'MEDIUM',
      status: 'SUBMITTED',
      totalAmount: payload.totalAmount || 25000,
      currency: payload.currency || 'USD',
      requiredDate: payload.requiredDate || '2026-10-30',
      items: payload.items || [],
      financialSummary: payload.financialSummary || {
        subtotal: 25000,
        taxAmount: 4500,
        totalAmount: 29500,
        currency: 'USD',
        budgetLimit: 100000,
        budgetConsumed: 30000,
        availableBudget: 70000,
        budgetStatus: 'SUFFICIENT',
      },
      sapValidation: {
        companyCode: true, plant: true, material: true, costCenter: true, glAccount: true,
        purchasingGroup: true, currency: true, quantity: true, deliveryDate: true, budget: true, isValid: true,
      },
      sapDetails: {
        sapStatus: 'NOT_CREATED',
        integrationStatus: 'PENDING',
      },
      lifecycle: [
        { key: 'SUBMITTED', label: 'PR Created', status: 'COMPLETED', timestamp: new Date().toISOString() },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    };

    this.records.unshift(newRecord);
    return newRecord;
  }

  async cancelPr(id: string, _reason?: string): Promise<PrRecord> {
    await delay(env.mockDelayMs);
    const pr = await this.getPrById(id);
    if (!pr) throw new Error('PR not found');
    pr.status = 'CANCELLED';
    pr.updatedAt = new Date().toISOString();
    return pr;
  }

  async retrySapSync(id: string): Promise<PrRecord> {
    await delay(env.mockDelayMs);
    const pr = await this.getPrById(id);
    if (!pr) throw new Error('PR not found');
    pr.status = 'SAP_CREATED';
    pr.sapDetails = {
      sapPrNumber: `45000${Math.floor(100 + Math.random() * 900)}`,
      sapStatus: 'CREATED',
      integrationStatus: 'SYNCED',
      lastSyncTimestamp: new Date().toISOString(),
    };
    pr.updatedAt = new Date().toISOString();
    notifyGlobal('SAP Sync Retried', `PR ${pr.prNumber} synced to SAP S/4HANA BAPI.`, 'System');
    return pr;
  }

  async createPurchaseRequisition(payload: any): Promise<{ success: boolean; prRecord: PrRecord }> {
    const pr = await this.createPr(payload);
    return { success: true, prRecord: pr };
  }

  syncPrRecord(pr: PrRecord, previousId?: string): PrRecord {
    const idx = this.records.findIndex(
      (r) => r.id === pr.id || (previousId && r.id === previousId) || r.prNumber === pr.prNumber || r.requestNumber === pr.requestNumber
    );
    if (idx >= 0) {
      const merged = Object.assign({}, this.records[idx], pr);
      this.records.splice(idx, 1);
      this.records.unshift(merged);
      return merged;
    }
    this.records.unshift(pr);
    return pr;
  }
}

export const mockPrManagementService = new MockPrManagementService();
